import React, { forwardRef, useId } from 'react';

import { cn } from '../../utils/cn';

interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  helperText?: string;
  error?: string;
  containerClassName?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      value,
      onChange,
      onKeyPress,
      placeholder,
      disabled = false,
      className,
      rows = 3,
      label,
      helperText,
      error,
      id,
      containerClassName,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const textAreaId = id ?? generatedId;
    const helperId = helperText ? `${textAreaId}-helper` : undefined;
    const errorId = error ? `${textAreaId}-error` : undefined;
    const { 'aria-describedby': ariaDescribedBy, ...textareaProps } = rest as {
      'aria-describedby'?: string;
    } & typeof rest;
    const describedBy = [helperId, errorId, ariaDescribedBy].filter(Boolean).join(' ');

    return (
      <div className={cn('stack-sm', containerClassName)}>
        {label ? (
          <label htmlFor={textAreaId} className="type-label">
            {label}
          </label>
        ) : null}
        <textarea
          id={textAreaId}
          ref={ref}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={cn(
            'min-h-[3rem] w-full resize-none rounded-2xl border border-[color:var(--color-neutral-200)] bg-[color:var(--color-neutral-0)] px-[var(--space-3)] py-[var(--space-2)] text-sm text-[color:var(--color-neutral-800)] shadow-sm transition-soft focus-visible:border-[color:var(--color-primary-400)] focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring-primary)]',
            error &&
              'border-[color:var(--color-error-400)] focus-visible:border-[color:var(--color-error-400)] focus-visible:ring-[color:var(--focus-ring-danger)]',
            className,
          )}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy || undefined}
          {...textareaProps}
        />
        {helperText ? (
          <span id={helperId} className="type-body-xs">
            {helperText}
          </span>
        ) : null}
        {error ? (
          <span
            id={errorId}
            className="text-xs font-semibold text-[color:var(--color-error-600)]"
            role="alert"
          >
            {error}
          </span>
        ) : null}
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';
